/**
 * Common Validation Schemas
 *
 * Zod schemas for validating common-related forms and API requests.
 * Provides client-side validation that matches server-side validation rules.
 *
 * Features:
 * - Email format validation
 * - Password strength validation
 * - Analytics query validation
 * - Report download validation
 */

import { z } from './zod';
import {
  getDifficultyOptions,
  getMuscleGroupOptions,
  getBodyPartOptions,
  getExerciseTypeOptions,
  getEquipmentOptions,
  createEnumFromOptions,
} from '../utils/select-options';

// =============================================================================
// BASIC FIELD SCHEMAS
// =============================================================================

/**
 * Email validation schema
 * Validates email format using Zod's built-in email validator
 */
export const emailSchema = z.string().email();

/**
 * Password validation schema
 * Requires minimum 12 characters for security
 */
export const passwordSchema = z.string().min(12);

/**
 * Name validation schema
 * Validates name format using Zod's built-in string validator
 */
export const nameSchema = z
  .string()
  .min(2, 'Name must be at least two characters')
  .max(100, 'Name must be less than 100 characters')
  .trim();

/**
 * Token validation schema
 * Validates token format using Zod's built-in string validator
 */
export const tokenSchema = z
  .string()
  .length(50, 'Token is invalid')
  .regex(/^[a-zA-Z0-9]+$/, 'Token is invalid')
  .trim();

// =============================================================================
// DOMAIN FIELD SCHEMAS (Workout/Template)
// =============================================================================

/**
 * Workout/template name validation
 * Used for workout names, plan names, exercise names
 */
export const templateNameSchema = z
  .string()
  .min(1, 'Name is required')
  .max(255, 'Name must be less than 255 characters')
  .trim();

/**
 * Template description validation
 * Used for plan/workout template descriptions (optional, longer limit)
 */
export const templateDescriptionSchema = z
  .string()
  .max(530, 'Description must be less than 530 characters')
  .nullable()
  .optional();

/**
 * Session description validation
 * Used for session-related descriptions (required, shorter limit)
 */
export const sessionDescriptionSchema = z
  .string()
  .min(1, 'Description is required')
  .max(255, 'Description must be less than 255 characters')
  .trim();

/**
 * Difficulty level validation
 */
export const difficultyLevelSchema = createEnumFromOptions(
  getDifficultyOptions()
);

/**
 * Muscle group validation
 */
export const muscleGroupSchema = createEnumFromOptions(getMuscleGroupOptions());

/**
 * Body part validation
 */
export const bodyPartSchema = createEnumFromOptions(getBodyPartOptions());

/**
 * Exercise type validation (cardio, strength, etc.)
 */
export const exerciseTypeSchema = createEnumFromOptions(
  getExerciseTypeOptions()
);

/**
 * Equipment validation
 */
export const equipmentSchema = createEnumFromOptions(getEquipmentOptions());

/**
 * Set type validation (full list of all set types)
 */
export const setTypeSchema = z.enum([
  'regular',
  'drop',
  'pyramid',
  'reverse_pyramid',
  'rest_pause',
  'cluster',
]);

/**
 * Group type validation (full list of all group types)
 */
export const groupTypeSchema = z.enum([
  'regular',
  'superset',
  'triset',
  'circuit',
  'giant_set',
  'emom',
]);

/**
 * Tracking type validation (how exercise progress is measured)
 */
export const trackingTypeSchema = z.enum([
  'reps_only',
  'weight_reps',
  'time_based',
  'distance_based',
  'time_distance',
  'weight_time',
]);

// =============================================================================
// CONTACT SCHEMA
// =============================================================================

export const contactUsSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  subject: z
    .string()
    .min(1, 'Subject is required')
    .max(250, 'Subject must be less than 250 characters')
    .trim(),
  message: z
    .string()
    .min(10, 'Message is required')
    .max(1000, 'Message must be less than 1000 characters'),
});

// =============================================================================
// WAITLIST SCHEMA
// =============================================================================

export const waitlistSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  goals: z
    .string()
    .max(500, 'Fitness goals must be less than 500 characters')
    .optional(),
});

// =============================================================================
// ANALYTICS VALIDATION SCHEMA
// =============================================================================

/**
 * Analytics query validation schema
 * Used for validating analytics API query parameters
 */
export const analyticsSchema = z.object({
  period: z.enum(['weekly', 'monthly']).optional(),
  window: z.enum(['7d', '30d', '90d', '6m', '1y', 'all']).optional(),
  count: z.number().min(1).max(50).optional(),
});

/**
 * Partial analytics schema for updates
 * All fields are optional for PATCH requests
 */
export const updateAnalyticsSchema = analyticsSchema.partial();

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type EmailInput = z.infer<typeof emailSchema>;

export type DifficultyLevel = z.infer<typeof difficultyLevelSchema>;
export type BodyPart = z.infer<typeof bodyPartSchema>;
export type TrackingType = z.infer<typeof trackingTypeSchema>;

export type ContactUsInput = z.input<typeof contactUsSchema>;
export type ContactUsOutput = z.output<typeof contactUsSchema>;

export type WaitlistInput = z.input<typeof waitlistSchema>;
export type WaitlistOutput = z.output<typeof waitlistSchema>;

export type AnalyticsInput = z.input<typeof analyticsSchema>;
export type AnalyticsOutput = z.output<typeof analyticsSchema>;
export type UpdateAnalyticsInput = z.input<typeof updateAnalyticsSchema>;
export type UpdateAnalyticsOutput = z.output<typeof updateAnalyticsSchema>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

/**
 * Validate email data
 * @param data - The data to validate
 * @returns The validated email string
 * @throws ZodError if validation fails
 */
export function validateEmail(data: unknown): string {
  return emailSchema.parse(data);
}

/**
 * Safely validate email data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateEmail(data: unknown) {
  return emailSchema.safeParse(data);
}

/**
 * Check if data is a valid email
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidEmail(data: unknown): boolean {
  return emailSchema.safeParse(data).success;
}

/**
 * Validate password data
 * @param data - The data to validate
 * @returns The validated password string
 * @throws ZodError if validation fails
 */
export function validatePassword(data: unknown): string {
  return passwordSchema.parse(data);
}

/**
 * Safely validate password data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidatePassword(data: unknown) {
  return passwordSchema.safeParse(data);
}

/**
 * Check if data is a valid password
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidPassword(data: unknown): boolean {
  return passwordSchema.safeParse(data).success;
}

/**
 * Validate token data
 * @param data - The data to validate
 * @returns The validated token string
 * @throws ZodError if validation fails
 */
export function validateToken(data: unknown): string {
  return tokenSchema.parse(data);
}

/**
 * Safely validate token data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateToken(data: unknown) {
  return tokenSchema.safeParse(data);
}

/**
 * Check if data is a valid token
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidToken(data: unknown): boolean {
  return tokenSchema.safeParse(data).success;
}

/**
 * Validate contact-us data
 * @param data - The data to validate
 * @returns The validated and typed login data
 * @throws ZodError if validation fails
 */
export function validateContactUs(data: unknown): ContactUsOutput {
  return contactUsSchema.parse(data);
}

/**
 * Safely validate contact-us data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateContactUs(data: unknown) {
  return contactUsSchema.safeParse(data);
}

/**
 * Check if data is valid contact-us data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidContactUs(data: unknown): boolean {
  return contactUsSchema.safeParse(data).success;
}

/**
 * Validate waitlist data
 * @param data - The data to validate
 * @returns The validated and typed login data
 * @throws ZodError if validation fails
 */
export function validateWaitlist(data: unknown): WaitlistOutput {
  return waitlistSchema.parse(data);
}

/**
 * Safely validate waitlist data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateWaitlist(data: unknown) {
  return waitlistSchema.safeParse(data);
}

/**
 * Check if data is valid waitlist data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidWaitlist(data: unknown): boolean {
  return waitlistSchema.safeParse(data).success;
}

/**
 * Validate complete analytics data
 * @param data - The data to validate
 * @returns The validated and typed analytics
 * @throws ZodError if validation fails
 */
export function validateAnalytics(data: unknown): AnalyticsOutput {
  return analyticsSchema.parse(data);
}

/**
 * Safely validate complete analytics data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateAnalytics(data: unknown) {
  return analyticsSchema.safeParse(data);
}

/**
 * Validate partial analytics data for updates
 * @param data - The data to validate
 * @returns The validated and typed partial analytics
 * @throws ZodError if validation fails
 */
export function validateUpdateAnalytics(data: unknown): UpdateAnalyticsOutput {
  return updateAnalyticsSchema.parse(data);
}

/**
 * Safely validate partial analytics data for updates
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateAnalytics(data: unknown) {
  return updateAnalyticsSchema.safeParse(data);
}

/**
 * Check if data is a valid analytics query
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidAnalytics(data: unknown): boolean {
  return analyticsSchema.safeParse(data).success;
}
